import type { SymmetricKey } from '@faceup/crypto'
import { encryptFileWithReportKey } from '@faceup/crypto'
import { useCryptoErrorHandler } from '../utils'

type EncryptFilesFn = (
  filesData: {
    formItemId: string
    files: File[]
  }[],
  reportKey?: SymmetricKey
) => Promise<Record<string, File[]>>

type UseReportBodyReturn = {
  encryptFiles: EncryptFilesFn
}

export const useReportFiles = (): UseReportBodyReturn => {
  const handleError = useCryptoErrorHandler()

  const encryptFiles: EncryptFilesFn = async (filesData, reportKey) => {
    try {
      if (!reportKey) {
        throw new Error('Symmetric key missing – cannot encrypt file attachment')
      }

      const encryptedFormItemsFiles: Record<string, File[]> = {}
      for (const fileFormItem of filesData) {
        const encryptedFiles = await Promise.all(
          fileFormItem.files.map(async file => {
            const encryptedFile = await encryptFileWithReportKey(file, reportKey)
            if (encryptedFile.isErr()) {
              throw new Error(`Failed to encrypt file attachment: ${encryptedFile.error.message}`)
            }
            return encryptedFile.value
          })
        )
        encryptedFormItemsFiles[fileFormItem.formItemId] = encryptedFiles
      }
      return encryptedFormItemsFiles
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Unknown error during file encryption'
      handleError(message)
      throw error
    }
  }

  return { encryptFiles }
}
